/**
 * The smallest markdown renderer that reads a terms document.
 *
 * The creator terms are stored and hashed as markdown text (`docs/terms/*.md`,
 * published into `PlatformTermsDocument`). The signup form shows them RAW —
 * what it shows is what it hashes — but the public `/terms` page is for
 * reading, and a wall of `##` and `**` is not that. Headings, paragraphs,
 * bullet lists and bold are the whole grammar those documents use; anything
 * else is left as written. No HTML is ever interpreted: the text becomes
 * React elements, never `innerHTML`.
 */

function inline(text: string, keyBase: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  parts.forEach((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      out.push(<strong key={`${keyBase}-${i}`}>{part.slice(2, -2)}</strong>);
    } else if (part.startsWith('`') && part.endsWith('`')) {
      out.push(<code key={`${keyBase}-${i}`}>{part.slice(1, -1)}</code>);
    } else if (part.length > 0) {
      out.push(part);
    }
  });
  return out;
}

export function renderMarkdown(source: string): React.ReactNode[] {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const nodes: React.ReactNode[] = [];
  let paragraph: string[] = [];
  let list: string[] = [];
  let key = 0;

  const flushParagraph = (): void => {
    if (paragraph.length === 0) return;
    const text = paragraph.join(' ');
    nodes.push(<p key={`p-${key++}`}>{inline(text, `p-${key}`)}</p>);
    paragraph = [];
  };
  const flushList = (): void => {
    if (list.length === 0) return;
    nodes.push(
      <ul key={`ul-${key++}`}>
        {list.map((item, i) => (
          <li key={i}>{inline(item, `li-${key}-${i}`)}</li>
        ))}
      </ul>,
    );
    list = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      const level = heading[1]!.length;
      const text = inline(heading[2]!, `h-${key}`);
      if (level === 1) nodes.push(<h1 key={`h-${key++}`}>{text}</h1>);
      else if (level === 2) nodes.push(<h2 key={`h-${key++}`}>{text}</h2>);
      else nodes.push(<h3 key={`h-${key++}`}>{text}</h3>);
      continue;
    }
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    if (bullet) {
      flushParagraph();
      list.push(bullet[1]!);
      continue;
    }
    if (line.trim().length === 0) {
      flushParagraph();
      flushList();
      continue;
    }
    if (list.length > 0 && /^\s{2,}/.test(raw)) {
      /* A wrapped bullet continues the previous item. */
      list[list.length - 1] = `${list[list.length - 1]} ${line.trim()}`;
      continue;
    }
    flushList();
    paragraph.push(line.trim());
  }
  flushParagraph();
  flushList();
  return nodes;
}
